import type { OverlapGroup } from '../../lib/routeGeometry';
import type { NodeConnections } from '../../types/nodeConfig';

interface Props {
  connections: NodeConnections;
  overlapGroups?: OverlapGroup[];
}

export default function ConnectionInfoPanel({
  connections,
  overlapGroups = [],
}: Props) {
  const overlappingRouteIds = new Set(overlapGroups.flatMap((g) => g.routeIds));

  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Connections</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-medium text-text-muted">Incoming routes</p>
          {connections.incoming.length === 0 ? (
            <p className="text-xs text-text-subtle">None</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {connections.incoming.map((c) => (
                <li key={c.route_id} className="text-text-muted">
                  <span className="text-text">{c.route_name}</span>
                  <span className="text-text-subtle"> from {c.peer_node_name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="mb-2 text-xs font-medium text-text-muted">Outgoing routes</p>
          {connections.outgoing.length === 0 ? (
            <p className="text-xs text-text-subtle">None</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {connections.outgoing.map((c) => (
                <li key={c.route_id} className="text-text-muted">
                  <span className="text-text">{c.route_name}</span>
                  <span className="text-text-subtle"> to {c.peer_node_name}</span>
                  {overlappingRouteIds.has(c.route_id) && (
                    <span className="ml-1 text-[10px] text-text-subtle">
                      (overlapping path)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {overlapGroups.length > 0 && (
        <p className="rounded-md border border-border-subtle bg-surface/60 px-3 py-2 text-xs text-text-subtle">
          {overlapGroups.map((group) => (
            <span key={group.routeIds.join('-')} className="block">
              {group.routeNames.join(' and ')} share the same path on the canvas
              (shown with a parallel offset).
            </span>
          ))}
        </p>
      )}
    </section>
  );
}
